import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react'
import { useAuth } from '../../context/useAuth'
import { useTimeline } from '../../context/useTimeline'
import { useWorkspace } from '../../context/useWorkspace'
import { getTour } from './TourRegistry'
import { runTourCallback } from './TourCallbackRegistry'
import { SpotlightPortal } from './SpotlightPortal'
import { TourGuideContext, type TourGuideContextValue } from './tourGuideContextCore'
import { hasSeenTour, markTourSeen } from './tourPersistence'
import { emitTourTelemetry } from './tourTelemetry'
import { evaluateInputCriteria, evaluateTourCondition, pathMatchesTourTemplate, resolveTourPath } from './tourGuideUtils'
import { useCurrentPath } from './useCurrentPath'
import type { StartTourOptions, TourDefinition } from './types'
import { isPersonalWorkspace } from '../../utils/workspaceKind'

export const TourGuideProvider = ({ children }: PropsWithChildren) => {
  const { user } = useAuth()
  const { elements } = useTimeline()
  const {
    activeWorkspaceId,
    workspaces,
    isSidebarPinned,
    setSidebarPinned,
    canPerform,
  } = useWorkspace()
  const { currentPath, navigate } = useCurrentPath()
  const userId = user?.uid ?? ''
  const actorName = user?.displayName ?? null
  const [activeTour, setActiveTour] = useState<TourDefinition | null>(null)
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [fulfilledStepIds, setFulfilledStepIds] = useState<Set<string>>(() => new Set())
  const [isTargetVisible, setIsTargetVisible] = useState(false)
  const preTourPinnedStateRef = useRef<boolean | null>(null)
  const stepTelemetryRef = useRef(new Set<string>())
  // Hold setSidebarPinned via a ref so the cleanup effect that restores the
  // sidebar doesn't fire when usePersistedState's setter identity changes
  // mid-tour (which would prematurely run restoration with a stale ref value).
  const setSidebarPinnedRef = useRef(setSidebarPinned)
  useEffect(() => { setSidebarPinnedRef.current = setSidebarPinned }, [setSidebarPinned])
  const activeWorkspace = workspaces.find((workspace) => workspace.id === activeWorkspaceId)
  const currentStep = activeTour?.steps[currentStepIndex] ?? null

  const telemetryBase = useCallback(() => ({
    workspaceId: activeWorkspaceId,
    actorId: userId,
    actorName,
  }), [activeWorkspaceId, actorName, userId])

  const restoreSidebar = useCallback(() => {
    if (preTourPinnedStateRef.current === null) return
    setSidebarPinnedRef.current(preTourPinnedStateRef.current)
    preTourPinnedStateRef.current = null
  }, [])

  const clearTour = useCallback(() => {
    restoreSidebar()
    setActiveTour(null)
    setCurrentStepIndex(0)
    setIsTargetVisible(false)
  }, [restoreSidebar])

  const endTour = useCallback(() => {
    if (!activeTour || !userId || !activeWorkspaceId) return
    markTourSeen(activeTour.id, 'completed', userId, activeWorkspaceId, activeTour.seenScope)
    emitTourTelemetry({ event: 'TOUR_COMPLETED', tourId: activeTour.id, ...telemetryBase() })
    void runTourCallback(activeTour.onCompleteAction, activeTour).catch((error) => {
      console.warn('[TOURGUIDE] Completion callback failed.', error)
    })
    clearTour()
  }, [activeTour, activeWorkspaceId, clearTour, telemetryBase, userId])

  const skipTourWithReason = useCallback((reason = 'dismissed') => {
    if (!activeTour || !userId || !activeWorkspaceId) return
    markTourSeen(activeTour.id, 'dismissed', userId, activeWorkspaceId, activeTour.seenScope)
    emitTourTelemetry({ event: 'TOUR_SKIPPED', tourId: activeTour.id, reason, ...telemetryBase() })
    clearTour()
  }, [activeTour, activeWorkspaceId, clearTour, telemetryBase, userId])

  const skipTour = useCallback(() => skipTourWithReason('dismissed'), [skipTourWithReason])

  const emitStepCompleted = useCallback((stepId: string) => {
    if (!activeTour || stepTelemetryRef.current.has(stepId)) return
    stepTelemetryRef.current.add(stepId)
    emitTourTelemetry({ event: 'TOUR_STEP_COMPLETED', tourId: activeTour.id, stepId, ...telemetryBase() })
  }, [activeTour, telemetryBase])

  const advanceStep = useCallback((userCompleted: boolean) => {
    if (!activeTour || !currentStep) return
    if (userCompleted) emitStepCompleted(currentStep.id)
    if (currentStepIndex >= activeTour.steps.length - 1) endTour()
    else {
      setIsTargetVisible(false)
      setCurrentStepIndex((index) => index + 1)
    }
  }, [activeTour, currentStep, currentStepIndex, emitStepCompleted, endTour])

  const startTour = useCallback((id: string, options?: StartTourOptions): boolean => {
    const tour = getTour(id)
    if (!tour || !userId || !activeWorkspaceId || activeTour) return false
    if (tour.requiredAction && !canPerform(tour.requiredAction)) return false
    if (!options?.force && hasSeenTour(id, userId, activeWorkspaceId, tour.seenScope)) return false
    preTourPinnedStateRef.current = tour.forcePinnedSidebar ? isSidebarPinned : null
    if (tour.forcePinnedSidebar) setSidebarPinned(true)
    stepTelemetryRef.current = new Set()
    setFulfilledStepIds(new Set())
    setCurrentStepIndex(0)
    setIsTargetVisible(false)
    setActiveTour(tour)
    emitTourTelemetry({ event: 'TOUR_STARTED', tourId: tour.id, ...telemetryBase() })
    return true
  }, [activeTour, activeWorkspaceId, canPerform, isSidebarPinned, setSidebarPinned, telemetryBase, userId])

  const previousStep = useCallback(() => {
    if (!activeTour) return
    setIsTargetVisible(false)
    setCurrentStepIndex((index) => {
      const next = Math.max(0, index - 1)
      // Clear fulfillment for the step we're leaving AND the one we're going
      // back to, so the user has to re-perform the gated action on re-entry
      // instead of seeing a pre-enabled Next button.
      const leavingId = activeTour.steps[index]?.id
      const enteringId = activeTour.steps[next]?.id
      setFulfilledStepIds((current) => {
        if (!leavingId && !enteringId) return current
        const updated = new Set(current)
        if (leavingId) updated.delete(leavingId)
        if (enteringId) updated.delete(enteringId)
        return updated
      })
      return next
    })
  }, [activeTour])

  const isNextEnabled = Boolean(currentStep && (
    !currentStep.action || fulfilledStepIds.has(currentStep.id)
  ))

  const nextStep = useCallback(() => {
    if (!currentStep || !isNextEnabled) return
    advanceStep(true)
  }, [advanceStep, currentStep, isNextEnabled])

  const conditionValues = useMemo<Record<string, unknown>>(() => {
    const goalsCount = elements.filter((element) => element.type === 'GOAL').length
    const isPersonal = isPersonalWorkspace(activeWorkspace)
    return {
      'user.goalsCount': goalsCount,
      'workspace.goalsCount': goalsCount,
      'workspace.isPersonal': isPersonal,
      'workspace.isTeam': !isPersonal,
    }
  }, [activeWorkspace, elements])

  const resolvedScreenPath = currentStep
    ? resolveTourPath(currentStep.screenPath, { slug: activeWorkspace?.slug ?? '' })
    : ''
  // Unresolved templates still contain a `:slug` placeholder; refuse to
  // navigate (would land on `/workspace/:slug/d` literally) and refuse to
  // claim the route is ready.
  const screenPathFullyResolved = Boolean(resolvedScreenPath) && !resolvedScreenPath.includes(':')
  const routeReady = Boolean(currentStep && screenPathFullyResolved && pathMatchesTourTemplate(currentPath, resolvedScreenPath))

  useEffect(() => {
    if (!currentStep || !evaluateTourCondition(currentStep.if, conditionValues)) return
    if (!screenPathFullyResolved) return
    if (!routeReady) navigate(resolvedScreenPath)
  }, [conditionValues, currentStep, navigate, resolvedScreenPath, routeReady, screenPathFullyResolved])

  useEffect(() => {
    if (!currentStep || evaluateTourCondition(currentStep.if, conditionValues)) return
    const timer = window.setTimeout(() => advanceStep(false), 0)
    return () => window.clearTimeout(timer)
  }, [advanceStep, conditionValues, currentStep])

  useEffect(() => {
    if (!currentStep || !routeReady || !isTargetVisible || !currentStep.action || fulfilledStepIds.has(currentStep.id)) return
    if (currentStep.action === 'wait') {
      const timer = window.setTimeout(() => advanceStep(false), currentStep.durationMs ?? 0)
      return () => window.clearTimeout(timer)
    }

    let cleanup: (() => void) | undefined
    let pollTimer: number | undefined
    const fulfill = () => {
      setFulfilledStepIds((current) => new Set(current).add(currentStep.id))
      emitStepCompleted(currentStep.id)
    }
    const attach = (): boolean => {
      const selector = currentStep.action === 'input' ? currentStep.selector : currentStep.targetSelector
      const element = selector ? document.querySelector<HTMLElement>(selector) : null
      if (!element) return false
      if (currentStep.action === 'click') {
        element.addEventListener('click', fulfill, { once: true })
        cleanup = () => element.removeEventListener('click', fulfill)
      } else {
        const input = element as HTMLInputElement | HTMLTextAreaElement
        const check = () => {
          if (evaluateInputCriteria(input.value ?? '', currentStep.criteria ?? '')) fulfill()
        }
        input.addEventListener('input', check)
        check()
        cleanup = () => input.removeEventListener('input', check)
      }
      return true
    }
    if (!attach()) pollTimer = window.setInterval(() => {
      if (attach() && pollTimer) window.clearInterval(pollTimer)
    }, 100)
    return () => {
      cleanup?.()
      if (pollTimer) window.clearInterval(pollTimer)
    }
  }, [advanceStep, currentStep, emitStepCompleted, fulfilledStepIds, isTargetVisible, routeReady])

  const handleTargetTimeout = useCallback(() => {
    if (!activeTour || !currentStep) return
    if (currentStep.critical) {
      emitTourTelemetry({
        event: 'TOUR_ERROR',
        tourId: activeTour.id,
        stepId: currentStep.id,
        targetSelector: currentStep.targetSelector,
        reason: 'critical_target_timeout',
        ...telemetryBase(),
      })
      skipTourWithReason('critical_target_timeout')
      return
    }
    if (import.meta.env.DEV) console.warn(`[TOURGUIDE] Target "${currentStep.targetSelector}" timed out; skipping step.`)
    advanceStep(false)
  }, [activeTour, advanceStep, currentStep, skipTourWithReason, telemetryBase])

  // Sign-out / workspace-switch guard: endTour and skipTour early-return when
  // userId or activeWorkspaceId go empty, which would otherwise leave the
  // portal rendered with no way to dismiss it. Force-clear here.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sign-out / workspace-switch force-clear guard
    if (activeTour && (!userId || !activeWorkspaceId)) clearTour()
  }, [activeTour, activeWorkspaceId, clearTour, userId])

  useEffect(() => () => restoreSidebar(), [restoreSidebar])

  const value = useMemo<TourGuideContextValue>(() => ({
    activeTour,
    currentStep,
    currentStepIndex,
    isTourActive: Boolean(activeTour),
    isNextEnabled,
    startTour,
    previousStep,
    nextStep,
    endTour,
    skipTour,
  }), [activeTour, currentStep, currentStepIndex, endTour, isNextEnabled, nextStep, previousStep, skipTour, startTour])

  return (
    <TourGuideContext.Provider value={value}>
      {children}
      {activeTour && currentStep && evaluateTourCondition(currentStep.if, conditionValues) && (
        <SpotlightPortal
          key={currentStep.id}
          step={currentStep}
          stepIndex={currentStepIndex}
          stepCount={activeTour.steps.length}
          routeReady={routeReady}
          isNextEnabled={isNextEnabled}
          onBack={previousStep}
          onNext={nextStep}
          onSkip={skipTour}
          onTargetTimeout={handleTargetTimeout}
          onTargetVisibilityChange={setIsTargetVisible}
        />
      )}
    </TourGuideContext.Provider>
  )
}
